// Saisie quotidienne — shared config for the arbitrage decision menu
// and ledger pill rendering. The menu is intentionally TRIMMED:
// Justifié→PRESENT, Congé, Maladie, Récup, Télétravail, ½ congé,
// Absence injustifiée. "Décompter" and fractional récup are PERMANENTLY out
// (product decision §9.1: SHIFTIN does not deduct pay/time on lateness — no
// time-deduction concept exists, do not build one).
//
// A decision is recorded via PATCH /attendance/:id/decision (decisionType
// [+ customCode]); ½ congé additionally sets status=CONGE_HALF via PUT so
// validate-day debits 0.5. The ledger only moves the leave balance at
// validate-day — never during arbitrage.

import { ABSENCE_INJUSTIFIEE_CODE } from '@pointage/shared';
import type { DecisionTypeDTO, DecisionType, DailyRosterEntry } from '../api/hooks.js';

export type Tone = 'ok' | 'info' | 'shf' | 'warn' | 'bad';

export interface DecisionOption {
  /** Stable identity of the option (built-ins: their letter hotkey;
   * custom types: their code). Used as React key and selection test. */
  key: string;
  /** Keyboard shortcut, uppercase. Absent past the ninth custom type. */
  hotkey?: string;
  title: string;
  desc: string;
  tone: Tone;
  decisionType: DecisionType;
  /** AttendanceStatus to set alongside the decision (½ congé). */
  status?: 'CONGE_HALF';
  /** custom decision code (Absence injustifiée, catalogue types). */
  customCode?: string;
}

export const ABSENCE_INJ_CODE = ABSENCE_INJUSTIFIEE_CODE;

// Single trimmed menu — applies to any row the server flags
// needsDecision (true absence, or a short <6h day).
export const DECISION_MENU: DecisionOption[] = [
  { key: 'J', hotkey: 'J', title: 'Justifié', desc: 'comptabilisé présent', tone: 'ok', decisionType: 'PRESENT' },
  { key: 'C', hotkey: 'C', title: 'Congé', desc: 'journée posée sur le solde', tone: 'shf', decisionType: 'CONGE' },
  { key: 'H', hotkey: 'H', title: '½ congé', desc: 'demi-journée (−0,5 j)', tone: 'shf', decisionType: 'CONGE', status: 'CONGE_HALF' },
  { key: 'M', hotkey: 'M', title: 'Maladie', desc: 'justificatif attendu', tone: 'info', decisionType: 'MALADIE' },
  { key: 'R', hotkey: 'R', title: 'Récup', desc: 'imputé au compteur récup', tone: 'info', decisionType: 'RECUP' },
  { key: 'T', hotkey: 'T', title: 'Télétravail', desc: 'journée validée à distance', tone: 'shf', decisionType: 'TT' },
  { key: 'A', hotkey: 'A', title: 'Absence inj.', desc: 'sans solde, signalée à la direction', tone: 'bad', decisionType: 'AUTRE', customCode: ABSENCE_INJ_CODE },
];

// The catalogue types (Paramètres → Types de décision) extend the trimmed
// menu instead of being invisible to arbitrage — a type nobody can pick is
// a type nobody defined. They are stored exactly like « Absence inj. »:
// decisionType AUTRE + the code. Built-in letter shortcuts are untouched;
// customs take digits 1…9, and any beyond the ninth stay clickable
// without a shortcut rather than stealing a letter.
export function buildDecisionMenu(types: readonly DecisionTypeDTO[] | undefined): DecisionOption[] {
  const customs = (types ?? []).filter((t) => !t.builtin && t.code !== ABSENCE_INJ_CODE);
  return [
    ...DECISION_MENU,
    ...customs.map((t, i) => ({
      key: t.code,
      ...(i < 9 ? { hotkey: String(i + 1) } : {}),
      title: t.label,
      desc: 'type personnalisé',
      tone: 'info' as Tone,
      decisionType: 'AUTRE' as DecisionType,
      customCode: t.code,
    })),
  ];
}

// Recommended ("hot") default per row situation.
export function recommendedOption(noClock: boolean): DecisionOption {
  // A true absence defaults to Congé; a short worked day defaults to Justifié.
  return noClock ? DECISION_MENU[1]! : DECISION_MENU[0]!;
}

// ── ledger pill rendering ────────────────────────────────────
export interface PillView {
  label: string;
  tone: Tone;
}

export function decisionPill(
  decisionType: DecisionType | null,
  customCode: string | null,
  status: string | null,
  customLabel?: (code: string) => string | undefined,
): PillView | null {
  if (!decisionType) return null;
  if (decisionType === 'AUTRE' && customCode === ABSENCE_INJ_CODE) {
    return { label: 'Absence inj.', tone: 'bad' };
  }
  if (decisionType === 'AUTRE') {
    return { label: customCode ? customLabel?.(customCode) ?? 'Autre' : 'Autre', tone: 'info' };
  }
  switch (decisionType) {
    case 'PRESENT': return { label: 'Présent', tone: 'ok' };
    case 'CONGE': return { label: status === 'CONGE_HALF' ? '½ Congé' : 'Congé', tone: 'shf' };
    case 'TT': return { label: 'Télétravail', tone: 'shf' };
    case 'MALADIE': return { label: 'Maladie', tone: 'info' };
    case 'RECUP': return { label: 'Récup', tone: 'info' };
    case 'RDV_PERSO': return { label: 'RDV perso', tone: 'info' };
    default: return { label: 'Décidé', tone: 'info' };
  }
}

// ── time helpers ─────────────────────────────────────────────
// decimalHours / minutes → "H:MM" (8.3h → "8:18", 0 → "0:00")
export function hhmmFromMinutes(mins: number | null | undefined): string {
  const m = Math.round(mins ?? 0);
  return `${Math.floor(m / 60)}:${String(m % 60).padStart(2, '0')}`;
}

// "HH:MM" clock string → minutes from midnight (null if unparseable)
export function clockToMinutes(hm: string | null | undefined): number | null {
  if (!hm) return null;
  const m = /^(\d{1,2}):(\d{2})$/.exec(hm);
  if (!m) return null;
  return Number(m[1]) * 60 + Number(m[2]);
}

// ── per-employee avatar colour (proto squircle palette) ──────
// The proto colours avatars per person (not per entity). Deterministic
// hash → fixed palette so a given employee keeps the same colour.
const AVATAR_PALETTE = [
  '#7037ef', '#0bb89a', '#0bb5ab', '#e0590c', '#c23ee0',
  '#3b6fe0', '#d6336c', '#0a9d72', '#9368f3', '#e8590c',
];
export function avatarColor(seed: string): string {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_PALETTE[h % AVATAR_PALETTE.length]!;
}

// ── row state (single source of truth) ──────────────────────
// A row needs arbitrage when the server flags a missing décision /
// prévenu? answer, OR when it is half-clocked (entrée XOR sortie) and
// still undecided — a sortie-sans-entrée must surface in "À arbitrer",
// never sit silently in "Traité" with an actionable "+ Définir".
export function isPendingRow(r: DailyRosterEntry): boolean {
  if (r.needsDecision || r.needsNotified) return true;
  const ci = r.record?.checkIn ?? null;
  const co = r.record?.checkOut ?? null;
  const halfClocked = (ci == null) !== (co == null); // exactly one side present
  const decided = !!r.record?.decisionType || !!r.autoDecision;
  return halfClocked && !decided;
}

// Detail line for the attention banner: "1 retard, 1 absence,
// 1 journée courte". Buckets the pending rows by their dominant issue.
export function pendingSummary(rows: DailyRosterEntry[]): string {
  let retards = 0;
  let absences = 0;
  let courtes = 0;
  let prevenu = 0;
  for (const r of rows.filter(isPendingRow)) {
    const noClock = !r.record?.checkIn && !r.record?.checkOut;
    if (r.tags.some((t) => t.type === 'late')) retards++;
    else if (noClock || r.tags.some((t) => t.type === 'abs')) absences++;
    else if (r.tags.some((t) => t.type === 'less')) courtes++;
    else if (r.needsNotified) prevenu++;
    else absences++; // half-clocked / other unresolved exception
  }
  const parts: string[] = [];
  if (retards) parts.push(`${retards} retard${retards > 1 ? 's' : ''}`);
  if (absences) parts.push(`${absences} absence${absences > 1 ? 's' : ''}`);
  if (courtes) parts.push(`${courtes} journée${courtes > 1 ? 's' : ''} courte${courtes > 1 ? 's' : ''}`);
  if (prevenu) parts.push(`${prevenu} réponse${prevenu > 1 ? 's' : ''} « prévenu ? »`);
  return parts.join(', ');
}

// ── write refusals on a signed-off day ───────────────────────
// The server closes a day at both levels (409 day_locked / day_in_review).
// The UI already hides the controls, but anything still in flight — a
// debounced note, an open arbitrage — must say WHY rather than « échec ».
export function attendanceWriteError(err: unknown, fallback: string): string {
  const body = (err as { body?: { error?: string; message?: string } } | null)?.body;
  if (body?.error === 'day_locked') return 'Journée validée — rouvrez-la avant de la modifier.';
  if (body?.error === 'day_in_review') {
    return 'Journée en revue — seule la validation finale peut encore la modifier.';
  }
  return body?.message ?? fallback;
}
